/**
 * Domain constants for the audit trail (ConfigChangeLog) list filters.
 */

import moment from 'moment'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { IsStringEmpty, isStringNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'

/** Action values accepted by {@code include} on the change log admin API. */
export const CONFIG_CHANGE_LOG_ACTION_VALUES: readonly string[] = [
    'INSERT',
    'UPDATE',
    'DELETE',
    'LOGIN',
    'LOGIN_FAILED',
    'LOGIN_AS',
    'SYNC_REQUESTED'
] as const

/**
 * Entity / table name values for the {@code type} query parameter, aligned with
 * {@code CActionAdminEntityListConfigChangeLog.CFilterData.m_pTypesFilterValues}.
 */
export const CONFIG_CHANGE_LOG_ENTITY_TYPE_VALUES: readonly string[] = [
    'ACDAgents',
    'ACDAgentsOwned',
    'Devices',
    'DevicesOwned',
    'DNISAssociations',
    'Codes',
    'CallRecordingIntegrations',
    'Partitions',
    'TelephoneSystems',
    'Users',
    'CRMConfiguration'
] as const

export const CONFIG_CHANGE_LOG_DEFAULT_SORT: string = '-EntryID'

export const CONFIG_CHANGE_LOG_PAGE_CHANGE_DIRECTION_NEXT: string = 'Next'

export const CONFIG_CHANGE_LOG_PAGE_CHANGE_DIRECTION_PREV: string = 'Prev'

/** Bundle key for the synthetic "all actions" label (not sent to the API). */
export const CONFIG_CHANGE_LOG_ACTION_DESCRIPTION_ALL_KEY: string = 'ConfigChangeLogList_Filter_ActionDescription_All'

/**
 * Maps each REST {@code include} action token to its {@link ConfigChangeLogListLanguage} bundle key
 * for {@link CLanguageHandler.get}.
 */
export const CONFIG_CHANGE_LOG_ACTION_DESCRIPTION_KEY_BY_VALUE: Readonly<Record<string, string>> = {
    INSERT: 'ConfigChangeLogList_Filter_ActionDescription_INSERT',
    UPDATE: 'ConfigChangeLogList_Filter_ActionDescription_UPDATE',
    DELETE: 'ConfigChangeLogList_Filter_ActionDescription_DELETE',
    LOGIN: 'ConfigChangeLogList_Filter_ActionDescription_LOGIN',
    LOGIN_FAILED: 'ConfigChangeLogList_Filter_ActionDescription_LOGIN_FAILED',
    LOGIN_AS: 'ConfigChangeLogList_Filter_ActionDescription_LOGIN_AS',
    SYNC_REQUESTED: 'ConfigChangeLogList_Filter_ActionDescription_SYNC_REQUESTED'
}

/** Bundle key for the synthetic "all entity types" label (not sent to the API). */
export const CONFIG_CHANGE_LOG_TYPE_DESCRIPTION_ALL_KEY: string = 'ConfigChangeLogList_Filter_TypeDescription_All'

/**
 * Maps each REST {@code type} table name to its {@link ConfigChangeLogListLanguage} bundle key
 * for {@link CLanguageHandler.get}.
 */
export const CONFIG_CHANGE_LOG_TYPE_DESCRIPTION_KEY_BY_VALUE: Readonly<Record<string, string>> = {
    ACDAgents: 'ConfigChangeLogList_Filter_TypeDescription_ACDAgents',
    ACDAgentsOwned: 'ConfigChangeLogList_Filter_TypeDescription_ACDAgentsOwned',
    Devices: 'ConfigChangeLogList_Filter_TypeDescription_Devices',
    DevicesOwned: 'ConfigChangeLogList_Filter_TypeDescription_DevicesOwned',
    DNISAssociations: 'ConfigChangeLogList_Filter_TypeDescription_DNISAssociations',
    Codes: 'ConfigChangeLogList_Filter_TypeDescription_Codes',
    CallRecordingIntegrations: 'ConfigChangeLogList_Filter_TypeDescription_CallRecordingIntegrations',
    Partitions: 'ConfigChangeLogList_Filter_TypeDescription_Partitions',
    TelephoneSystems: 'ConfigChangeLogList_Filter_TypeDescription_TelephoneSystems',
    Users: 'ConfigChangeLogList_Filter_TypeDescription_Users',
    CRMConfiguration: 'ConfigChangeLogList_Filter_TypeDescription_CRMConfiguration'
}

/** Wire format for {@link formatChangeLogUntilForApi}; matches {@link AdminFiltersOutlinedDateTime} output. */
export const CONFIG_CHANGE_LOG_UNTIL_API_FORMAT: string = 'YYYY-MM-DD HH:mm:ss'

/**
 * <p>Normalizes the Before filter value for {@code GET /v1/admin/changeLog?until=...} so the WS layer can parse it.</p>
 *
 * <p>{@code CReqRespChangeLogList.DateTimeParse} treats a string as having a time only when it ends with
 * {@code HH:mm:ss}; ISO-8601 values such as {@code 2024-06-02T23:59:59.000Z} therefore received an extra
 * {@code 00:00:00} suffix and failed to parse. This helper converts any moment-parsable value to the
 * {@link CONFIG_CHANGE_LOG_UNTIL_API_FORMAT} pattern in local time.</p>
 *
 * @param raw - Value from filter state (may be legacy ISO from older clients).
 * @returns Trimmed API string, or {@link EMPTY_STRING} when blank.
 */
export const formatChangeLogUntilForApi = (raw: string): string => {
    const trimmed: string = raw.trim()

    if (!isStringNotEmpty(trimmed)) {
        return EMPTY_STRING
    }

    const parsed: moment.Moment = moment(trimmed)

    if (!parsed.isValid()) {
        return trimmed
    }

    return parsed.format(CONFIG_CHANGE_LOG_UNTIL_API_FORMAT)
}

/**
 * Returns a telephony server or partition column value, or the localized {@code noneLabel}
 * when the API omits both description and id (including blank or whitespace-only strings).
 *
 * @param raw - Description or id string from the change log row, if any.
 * @param noneLabel - Localized label for an empty scope (typically {@code GenericAdminFilterLanguage_OPTION_NONE_LABEL}).
 * @returns Display string for the table or detail view.
 */
export const configChangeLogTelSysOrPartitionDisplay = (raw: Nullable<string>, noneLabel: string): string => {
    const value: string = raw ?? EMPTY_STRING

    if (IsStringEmpty(value.trim())) {
        return noneLabel
    }

    return value
}
